import React, { useState } from 'react'
import WordWithTooltip from '../tooltip/word-with-tooltip'
import { useTranslit } from 'hooks/useTranslit'
import { SentenceWithTranslit } from '../../lib/interfaces'
import ExampleSentencesHeader from './example-sentences-header'

type Props = {
   exampleSentences: SentenceWithTranslit[]
   title?: string
   // Embedded lists (under a single meaning) render without the section
   // chrome and heading; the default is the standalone word-level section.
   embedded?: boolean
   // The Thai word(s) picked out in coral where they appear in each sentence:
   // the page's headword on the Thai page, and the sense's Thai translations on
   // the English page. Only exact standalone-token matches highlight.
   headword?: string | string[]
   // The same, for the English line: the English page's headword, picked out
   // where it appears in the translation. Set on the English page only — the
   // Thai page's word is the Thai one.
   engHeadword?: string
}

const renderThaiSentence = (sentence: SentenceWithTranslit, headword?: string | string[]) => {
   if (!sentence.words) return null

   const headwords = headword === undefined ? [] : [headword].flat()

   return (
      <span>
         {sentence.words.map((word, index) => {
            // Separable verbs (ลอง … ดู) split into parts that each carry the
            // canonical entry as groupWord, so neither part matches literally.
            const isHeadword = headwords.some(
               (term) => word.word === term || word.groupWord === term
            )
            return isHeadword ? (
               // Important, because the global `.thai, .t2e` colour is unlayered
               // and so beats any utility-layer rule on specificity alone.
               <span className="[&_.t2e]:!text-exampleWord" key={index}>
                  <WordWithTooltip wordObject={word} />
               </span>
            ) : (
               <WordWithTooltip wordObject={word} key={index} />
            )
         })}
      </span>
   )
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Split the sentence around every standalone occurrence of the headword, the
// capture group keeping the matches in the result: "I like to run" →
// ['I like to ', 'run', '']. Word boundaries keep "run" out of "running" but
// still catch it in "family-run". A headword whose edges aren't word
// characters simply never matches, which leaves the sentence unhighlighted.
const splitByEngHeadword = (text: string, headword: string) =>
   text.split(new RegExp(`\\b(${escapeRegExp(headword)})\\b`, 'gi'))

const renderEngSentence = (sentence: SentenceWithTranslit, headword?: string) => {
   const text = sentence.engSentence.replace(/ \./, '.')
   const term = headword?.trim()
   if (!term) return <span>{text}</span>

   // Odd indexes are the matches; the case in the sentence is kept, so a
   // headword at the start of a sentence stays capitalised.
   return (
      <span>
         {splitByEngHeadword(text, term).map((part, index) =>
            index % 2 === 1 ? (
               <span className="text-exampleWord font-semibold" key={index}>
                  {part}
               </span>
            ) : (
               part
            )
         )}
      </span>
   )
}

// Embedded per-sense lists keep the sense scannable: show a few, reveal the
// rest on demand. The standalone word-level section shows everything it has.
const INITIAL_EMBEDDED_VISIBLE = 5

const ExampleSentencesList = (props: Props) => {
   const translit = useTranslit()
   const [expanded, setExpanded] = useState(false)

   if (!props.exampleSentences.length) return null

   const isCollapsible = props.embedded && props.exampleSentences.length > INITIAL_EMBEDDED_VISIBLE
   const visibleSentences =
      isCollapsible && !expanded
         ? props.exampleSentences.slice(0, INITIAL_EMBEDDED_VISIBLE)
         : props.exampleSentences
   const hiddenCount = props.exampleSentences.length - INITIAL_EMBEDDED_VISIBLE

   const list = (
      <div className={`flex flex-col gap-[12px] ${props.embedded ? 'mt-[12px] !gap-0' : ''}`}>
         {visibleSentences.map((sentence, index) => {
            const translitLine = translit.of(sentence.phonetic)

            return (
               <div
                  className={`flex items-start gap-[12px] bg-exampleBubble border border-exampleBubbleBorder rounded-[16px_16px_16px_5px] p-[12px_15px_13px] transition-colors duration-150 hover:border-[#cfe0f5] ${
                     props.embedded
                        ? '!items-start !gap-[16px] !bg-transparent !border-0 !border-t !border-t-hairline !rounded-none !py-[18px] !px-0 hover:!border-t-hairline'
                        : ''
                  }`}
                  key={index + sentence.id}
               >
                  <div className="flex-1 min-w-0">
                     <span
                        className={`font-thai font-normal text-thaiWord block ${props.embedded ? 'text-[24px] leading-[1.4]' : 'text-[22px] leading-[1.55]'}`}
                     >
                        {renderThaiSentence(sentence, props.headword)}
                     </span>
                     {translitLine && (
                        <div
                           className={`font-translit font-normal italic text-translit ${props.embedded ? 'text-[16px] mt-[6px]' : 'text-[15px] mt-[4px]'}`}
                        >
                           {translitLine}
                        </div>
                     )}
                     <div
                        className={`font-normal text-bodyGray ${props.embedded ? 'text-[16px] mt-[5px]' : 'text-[15px] mt-[2px]'}`}
                     >
                        {renderEngSentence(sentence, props.engHeadword)}
                     </div>
                  </div>
               </div>
            )
         })}
         {isCollapsible && !expanded && (
            <button
               type="button"
               className="self-start inline-flex items-center gap-[7px] mt-[1px] p-[2px] border-none bg-transparent text-translit text-[13px] font-bold cursor-pointer hover:underline"
               onClick={() => setExpanded(true)}
            >
               <span className="w-[18px] h-[18px] shrink-0 inline-flex items-center justify-center rounded-full bg-lightBlueTransparent text-[15px] leading-none no-underline">
                  +
               </span>
               Show {hiddenCount} more {hiddenCount === 1 ? 'example' : 'examples'}
            </button>
         )}
      </div>
   )

   if (props.embedded) return list

   return (
      <section id="example-sentences" className="mt-[38px]">
         <ExampleSentencesHeader title={props.title} variant="standalone" />
         {list}
      </section>
   )
}

export default ExampleSentencesList
